<template>
  <div>
    <app-spinner v-if="!is_page_loaded"/>
    <template v-else>
      <h1 class="title title--mb15">Clients Dashboard</h1>
      <h2 class="title title--mb15">Hotels mismatch</h2>
      <v-client-table
        :data="table_mismatch.items"
        :columns="table_mismatch.columns"
        :options="table_mismatch.options"
        class="table-default"
        :class="{ 'table-default--loading': table_mismatch_preloader }"
      >
        <template #h__status>
          <div class="table-default__left">Status</div>
        </template>
        <template #h__name>
          <div class="table-default__left">Name</div>
        </template>
        <template #h__group>
          <div class="table-default__left">Group</div>
        </template>
        <template #status="{ row }">
          <div class="table-default__left">
            <span class="status-tag" :style="{ 'backgroundColor': row.status.color }">{{ row.status.name }}</span>
          </div>
        </template>
        <template #name="{ row }">
          <div class="table-default__left table-default__controls">
            <span
              v-tooltip="{ content: row.note, placement: 'top-end', offset: 10 }"
              class="table-default__activity table-default__activity--notes"
              :class="{'table-default__activity--active': row.note}"
            ></span>
            <span
              :class="`fflag ff-md fflag-${row.country ? row.country.code : 'empty'}`"
              v-tooltip="{ content: row.country ? row.country.name : 'Not provided', placement: 'top-start', offset: 10 }"
            ></span>
            <router-link :to="{name: 'leads-detail-lead', params: {id: row.id }}" class="table-default__link-border">
              {{ row.name }}
            </router-link>
            <span v-if="row.pax">({{ row.pax }})</span>
            <span v-if="row.tour_operator"
                  v-tooltip="{ content: row.tour_operator.name, placement: 'top-start', offset: 10 }">
              ({{ row.tour_operator.short_name }})
            </span>
            <span v-if="row.is_double_request" class="status-tag" style="background-color: #439ad1">DR</span>
          </div>
        </template>
        <template #group="{ row }">
          <router-link v-if="row.client_group"
                       :to="{name: 'clients-groups-general', params: {id: row.client_group.id }}"
                       class="table-default__link-border">
            {{ row.client_group.name }}
          </router-link>
          <div v-else class="table-default__left">-</div>
        </template>
        <template #travel_time="{ row }">
          {{ dateFormatMonth(row.travel_time) }}
        </template>
        <template #manager="{ row }">
          <div class="table-default__controls">
            <div v-if="row.manager" class="table-default__photo">
              <img
                :src="getUserAvatar(row.manager.crop.crop_url)" alt="Avatar"
                v-tooltip="{ content: row.manager.full_name, placement: 'top-start', offset: 10 }"
              >
            </div>
            <span v-else>-</span>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickMismatchPagination"
            v-if="pagination_mismatch.pages > 1"
            v-model="pagination_mismatch.page"
            :page-count="pagination_mismatch.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination_mismatch.count" :count="table_mismatch.items.length"
                           :page="pagination_mismatch.page"/>
      </app-cells>
    </template>
  </div>
</template>

<script>

import { getLeads } from '@/http'
import { format, parse } from 'date-fns'

export default {
  name: 'ClientsDashboard',
  data() {
    return {
      is_page_loaded: false,
      table_mismatch_preloader: false,
      table_mismatch: {
        items: [],
        columns: [
          'status',
          'name',
          'group',
          'travel_time',
          'manager',
        ],
        options: {
          headings: {
            status: 'Status',
            name: 'Name',
            group: 'Group',
            travel_time: 'Travel time',
            manager: 'Manager',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_mismatch: {
        pages: 1,
        page: 1,
        count: 1,
      },
    }
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_sales_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_supervisor')
    },
    is_sales_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_manager')
    },
  },
  created() {
    if (!this.is_general_manager && !this.is_sales_supervisor) this.table_mismatch.columns.pop()
    this.fetchMismatchItems()
  },
  methods: {
    fetchMismatchItems() {
      getLeads({
        page: this.pagination_mismatch.page,
        page_size: 20,
        problem: 'hotels_mismatch',
        my: this.is_sales_manager ? true : '',
      }).then(response => {
        this.table_mismatch.items = response.data.results
        this.pagination_mismatch.pages = response.data.pages
        this.pagination_mismatch.count = response.data.count
        this.is_page_loaded = true
        this.table_mismatch_preloader = false
      })
    },
    onClickMismatchPagination(page) {
      this.table_mismatch_preloader = true
      this.pagination_mismatch.page = page
      this.fetchMismatchItems()
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    dateFormatMonth(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'MMM yyyy') : '-'
    },
  }
}
</script>
